import { useEffect, useState } from "react";
import { Search, Play } from "lucide-react";
import { api } from "@/lib/api";
import { PeepingLogo } from "@/components/PeepingLogo";
import { MediaImage, MediaVideo, LockedOverlay } from "@/components/Media";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const ACCESS_LABEL = { public: "Preview", subscriber: "Subscriber", member: "Members Only", creator: "Creator" };

export default function Videos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api
      .get("/content/videos")
      .then((r) => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="videos-screen" className="px-5 pt-6">
      <header className="flex items-center justify-between">
        <PeepingLogo className="w-28 items-start" showTagline={false} />
        <Search size={20} className="text-white/80" />
      </header>

      <h1 className="mt-4 text-2xl font-extrabold">Videos</h1>

      {/* Creator-published videos */}
      <section className="mt-5">
        {loading ? (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-2xl bg-white/5" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="videos-empty" className="rounded-2xl glass p-8 text-center">
            <p className="text-sm font-semibold">Exclusive videos are coming soon.</p>
            <p className="mt-1 text-xs text-[#a7a0af]">New releases from creators will appear here.</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {items.map((v) => (
              <li key={v.id} data-testid={`video-row-${v.id}`} className="flex items-center gap-3 rounded-2xl glass p-2.5">
                <button
                  onClick={() => !v.locked && v.video_url && setPlaying(v)}
                  className="relative h-16 w-24 shrink-0 overflow-hidden rounded-xl"
                  data-testid={`video-play-${v.id}`}
                >
                  <MediaImage url={v.cover_url} alt={v.title} className="h-full w-full object-cover" />
                  {v.locked ? (
                    <LockedOverlay />
                  ) : (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                      <Play size={18} fill="currentColor" />
                    </span>
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{v.title}</p>
                  <p className="truncate text-xs text-[#a7a0af]">{v.category}</p>
                  {v.locked && <span className="text-[0.6rem] font-semibold text-[#d42be8]">{ACCESS_LABEL[v.access_level]}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="max-w-lg border-white/10 bg-[#0c0a10] text-white">
          <DialogTitle>{playing?.title}</DialogTitle>
          {playing?.video_url && <MediaVideo url={playing.video_url} className="w-full rounded-xl" />}
          <p className="text-xs text-[#a7a0af]">{playing?.description}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
